import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { serviceAPI } from '../lib/api';
import { Tag, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function SellDomainPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', domain_name: '', message: '', budget: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await serviceAPI.request({ ...form, service_type: 'sell_domain' });
      setSubmitted(true);
      toast.success('Listing request submitted!');
    } catch { toast.error('Failed to submit'); }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Listing Request Received!</h3>
          <p className="text-zinc-500">Our team will review your domain and get back to you within 24 hours.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="sell-domain-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <Tag className="h-10 w-10 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('sellDomain.title')}</h1>
          <p className="text-zinc-500 mt-3">{t('sellDomain.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {[
            { title: '10-15%', desc: 'Commission on sales', sub: 'Industry-lowest rates' },
            { title: '24/7', desc: 'Global marketplace', sub: 'Reach buyers worldwide' },
            { title: 'Escrow', desc: 'Secure transactions', sub: 'Protected payments' },
          ].map((item, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm text-center">
              <div className="text-2xl font-bold text-[#00E5FF] font-['Outfit']">{item.title}</div>
              <div className="text-sm text-white mt-1">{item.desc}</div>
              <div className="text-xs text-zinc-600 mt-1">{item.sub}</div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="sell-domain-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="sell-name" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="sell-email" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.domain')}</Label>
              <Input value={form.domain_name} onChange={(e) => setForm(p => ({ ...p, domain_name: e.target.value }))} required
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="yourdomain.com" data-testid="sell-domain" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Asking Price ($)</Label>
              <Input value={form.budget} onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))}
                className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" placeholder="Optional" data-testid="sell-price" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.description')}</Label>
            <Textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} rows={3}
              className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none" data-testid="sell-description" />
          </div>
          <Button type="submit" disabled={loading}
            className="h-11 px-8 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="sell-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>List My Domain <ArrowRight className="ml-2 h-4 w-4" /></>}
          </Button>
        </form>
      </div>
    </div>
  );
}
